<template>
  <v-container fluid>
    <!-- Loading overlay global -->
    <v-overlay
      v-model="loadingStats"
      class="align-center justify-center"
      persistent
    >
      <div class="d-flex flex-column align-center">
        <v-progress-circular
          color="#ffb500"
          indeterminate
          size="64"
        ></v-progress-circular>
        <div class="text-white mt-4 text-h6">
          {{ $t("projections.loadingStats") }}
        </div>
      </div>
    </v-overlay>

    <v-card>
      <v-card-title class="text-h5 pa-4">
        <v-btn
          :icon="mdiArrowLeft"
          variant="text"
          @click="goBack"
          class="mr-2"
        ></v-btn>
        {{ $t("projections.formTitle") }}
      </v-card-title>

      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          closable
          class="mb-4"
          @click:close="clearError"
        >
          {{ error }}
        </v-alert>

        <!-- Informations client -->
        <v-card variant="outlined" class="mb-4">
          <v-card-title class="text-subtitle-1 bg-grey-lighten-4">
            {{ $t("projections.customerInfo") }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <div class="text-caption text-grey">
                  {{ $t("projections.customerNumber") }}
                </div>
                <div class="text-h6">{{ selectedCustomer?.number }}</div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="text-caption text-grey">
                  {{ $t("projections.customerName") }}
                </div>
                <div class="text-h6">{{ selectedCustomer?.name }}</div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="text-caption text-grey">
                  {{ $t("projections.currentStatus") }}
                </div>
                <v-chip
                  :color="getStatusColor(effectiveCurrentStatus)"
                  class="mt-1"
                >
                  {{ getStatusLabel(effectiveCurrentStatus) }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Statistiques année en cours (N-1) -->
        <v-card variant="outlined" class="mb-4" v-if="currentYearStats">
          <v-card-title class="text-subtitle-1 bg-grey-lighten-4">
            {{ $t("projections.currentYearStats") }} ({{ currentYear - 1 }})
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="6" md="3">
                <div class="text-caption text-grey">
                  {{ $t("projections.totalSales") }}
                </div>
                <div class="text-h6">
                  {{ formatCurrency(currentYearStats?.currentYearSales) }}
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="text-caption text-grey">
                  {{ $t("projections.totalPoints") }}
                </div>
                <div class="text-h6">
                  <v-chip color="#ffb500" size="small">
                    <span style="color: black; font-weight: 600">
                      {{ formatNumber(currentYearStats?.currentTotalPoints) }}
                      pts
                    </span>
                  </v-chip>
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="text-caption text-grey">
                  {{ $t("projections.calculatedStatus") }}
                </div>
                <div class="text-h6">
                  <v-chip
                    :color="
                      getStatusColor(currentYearStats?.currentStatus)
                    "
                    size="small"
                  >
                    {{ getStatusLabel(currentYearStats?.currentStatus) }}
                  </v-chip>
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="text-caption text-grey">
                  {{ $t("projections.projectionYear") }}
                </div>
                <div class="text-h6">{{ currentYear }}</div>
              </v-col>
            </v-row>

            <!-- Répartition des points par catégorie -->
            <v-divider class="my-3"></v-divider>
            <div class="text-caption text-grey mb-2">
              Répartition CA et points par catégorie
            </div>
            <v-row>
              <v-col cols="6" md="3">
                <div class="text-caption text-grey">Capital (Équipements)</div>
                <div class="text-body-2 font-weight-medium mb-1">
                  {{ formatCurrency(currentYearStats?.projectedCapitalSales) }}
                </div>
                <div class="text-body-1">
                  <v-chip color="blue-lighten-3" size="x-small">
                    <span style="color: black; font-weight: 500">
                      {{ formatNumber(currentYearStats?.currentCapitalPoints) }}
                      pts
                    </span>
                  </v-chip>
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="text-caption text-grey">Consommables</div>
                <div class="text-body-2 font-weight-medium mb-1">
                  {{
                    formatCurrency(currentYearStats?.projectedConsommableSales)
                  }}
                </div>
                <div class="text-body-1">
                  <v-chip color="green-lighten-3" size="x-small">
                    <span style="color: black; font-weight: 500">
                      {{
                        formatNumber(currentYearStats?.currentConsommablePoints)
                      }}
                      pts
                    </span>
                  </v-chip>
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="text-caption text-grey">Implants</div>
                <div class="text-body-2 font-weight-medium mb-1">
                  {{ formatCurrency(currentYearStats?.projectedImplantSales) }}
                </div>
                <div class="text-body-1">
                  <v-chip color="purple-lighten-3" size="x-small">
                    <span style="color: black; font-weight: 500">
                      {{ formatNumber(currentYearStats?.currentImplantPoints) }}
                      pts
                    </span>
                  </v-chip>
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="text-caption text-grey">Services</div>
                <div class="text-body-2 font-weight-medium mb-1">
                  {{ formatCurrency(currentYearStats?.projectedServiceSales) }}
                </div>
                <div class="text-body-1">
                  <v-chip color="orange-lighten-3" size="x-small">
                    <span style="color: black; font-weight: 500">
                      {{ formatNumber(currentYearStats?.currentServicePoints) }}
                      pts
                    </span>
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Formulaire de projection -->
        <v-form ref="form" v-model="valid">
          <div class="text-h6 mb-4">
            {{ $t("projections.salesProjections") }}
          </div>

          <v-row>
            <!-- Capital (Équipements) -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="form.capitalProjection"
                :label="$t('projections.capitalProjection')"
                type="number"
                variant="outlined"
                density="comfortable"
                prefix="€"
                :hint="$t('projections.capitalHint')"
                persistent-hint
              ></v-text-field>
            </v-col>

            <!-- Implants -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="form.implantProjection"
                :label="$t('projections.implantProjection')"
                type="number"
                variant="outlined"
                density="comfortable"
                prefix="€"
                :hint="$t('projections.implantHint')"
                persistent-hint
              ></v-text-field>
            </v-col>

            <!-- Services -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="form.serviceProjection"
                :label="$t('projections.serviceProjection')"
                type="number"
                variant="outlined"
                density="comfortable"
                prefix="€"
                :hint="$t('projections.serviceHint')"
                persistent-hint
              ></v-text-field>
            </v-col>

            <!-- Consommables -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="form.consumableProjection"
                :label="$t('projections.consumableProjection')"
                type="number"
                variant="outlined"
                density="comfortable"
                prefix="€"
                :hint="$t('projections.consumableHint')"
                persistent-hint
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Options de calcul -->
          <v-row class="mt-4">
            <!-- Choix du mode de vue -->
            <v-col cols="12" md="6">
              <v-card variant="outlined" class="h-100">
                <v-card-title class="text-subtitle-1">
                  {{ $t("projections.viewMode") }}
                </v-card-title>
                <v-card-text>
                  <v-radio-group v-model="selectedViewMode">
                    <v-radio
                      :label="$t('projections.simpleView')"
                      value="simple"
                      color="#ffb500"
                    ></v-radio>
                    <v-radio
                      :label="$t('projections.comparisonView')"
                      value="comparison"
                      color="#ffb500"
                    ></v-radio>
                  </v-radio-group>
                  <v-alert
                    v-if="selectedViewMode === 'comparison'"
                    type="info"
                    variant="tonal"
                    density="compact"
                  >
                    {{ $t("projections.comparisonModeInfo") }}
                  </v-alert>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Forcer un statut spécifique (optionnel) -->
            <v-col cols="12" md="6">
              <v-card variant="outlined" class="h-100">
                <v-card-title class="text-subtitle-1">
                  Forcer un statut spécifique (optionnel)
                </v-card-title>
                <v-card-text>
                  <v-select
                    v-model="form.overrideStatusId"
                    :items="statusOptions"
                    item-title="label"
                    item-value="value"
                    label="Statut forcé"
                    variant="outlined"
                    density="comfortable"
                    clearable
                    hint="Laissez vide pour laisser le calcul automatique déterminer le statut"
                    persistent-hint
                  >
                    <template v-slot:item="{ props, item }">
                      <v-list-item v-bind="props">
                        <template v-slot:prepend>
                          <v-chip
                            :color="getStatusColor(item.raw.value)"
                            size="x-small"
                            class="mr-2"
                          ></v-chip>
                        </template>
                      </v-list-item>
                    </template>
                    <template v-slot:selection="{ item }">
                      <v-chip
                        :color="getStatusColor(item.raw.value)"
                        size="small"
                      >
                        {{ item.raw.label }}
                      </v-chip>
                    </template>
                  </v-select>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Total -->
          <v-card
            variant="outlined"
            class="mt-4"
            style="background-color: #fff5e6"
          >
            <v-card-text>
              <v-row align="center">
                <v-col>
                  <div class="text-h6">
                    {{ $t("projections.totalProjectedSales") }}:
                    <span class="font-weight-bold" style="color: #ffb500">
                      {{ formatCurrency(totalProjectedSales) }}
                    </span>
                    <div class="text-caption text-grey mt-1">
                      {{ $t("projections.addedToLastYear") }}
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-btn variant="outlined" @click="goBack">
          {{ $t("cancel") }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="#ffb500"
          variant="elevated"
          :loading="loading"
          :disabled="!valid || !isFormComplete"
          @click="calculate"
          :prepend-icon="mdiCalculator"
          class="text-white"
        >
          {{ $t("projections.calculate") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import { mdiArrowLeft, mdiCalculator } from "@mdi/js";
import { getStatusColor } from "@/utils/statusColors";

export default {
  name: "ProjectionForm",
  data() {
    return {
      valid: false,
      mdiArrowLeft,
      mdiCalculator,
      selectedViewMode: "simple", // Mode de vue sélectionné
      loadingStats: false, // Indicateur de chargement des stats
      statusOptions: [
        { value: 0, label: "Hors programme" },
        { value: 1, label: "Statut 1" },
        { value: 2, label: "Statut 2" },
        { value: 3, label: "Statut 3" },
        { value: 4, label: "Statut 4" },
      ],
      form: {
        projectionYear: new Date().getFullYear(),
        capitalProjection: 0,
        implantProjection: 0,
        serviceProjection: 0,
        consumableProjection: 0,
        overrideStatusId: null,
      },
      rules: {
        required: (v) => !!v || this.$t("validations.required"),
        positive: (v) => v >= 0 || this.$t("validations.positive"),
      },
    };
  },
  computed: {
    ...mapState("projections", [
      "selectedCustomer",
      "loading",
      "error",
      "currentYearStats",
      "currentYear",
    ]),
    ...mapGetters("projections", [
      "validProjectionYears",
      "totalProjectedSales",
    ]),
    isFormComplete() {
      return this.form.projectionYear;
    },
    // Un statut forcé à la main (achats hors système comptabilisés en externe par un
    // admin) prévaut toujours sur le statut recalculé automatiquement à partir des seules
    // commandes suivies — même priorité que celle déjà appliquée ailleurs dans l'app.
    effectiveCurrentStatus() {
      return (
        this.selectedCustomer?.manualStatusCurrentYear ??
        this.currentYearStats?.currentStatus
      );
    },
  },
  mounted() {
    if (this.selectedCustomer) {
      this.form.customerId = this.selectedCustomer.id;
      // Charger les statistiques de l'année en cours
      this.loadStats();
    }
  },
  methods: {
    ...mapActions("projections", [
      "calculateProjection",
      "updateForm",
      "fetchCurrentYearStats",
      "resetProjectionFields",
    ]),
    async loadStats() {
      this.loadingStats = true;
      try {
        await this.fetchCurrentYearStats();
      } finally {
        this.loadingStats = false;
      }
    },
    async calculate() {
      if (this.$refs.form.validate()) {
        const formData = {
          customerId: this.selectedCustomer.id,
          ...this.form,
        };
        this.updateForm(formData);

        try {
          await this.calculateProjection();
          // Réinitialiser les champs après le calcul
          this.form.capitalProjection = 0;
          this.form.implantProjection = 0;
          this.form.serviceProjection = 0;
          this.form.consumableProjection = 0;
          this.form.overrideStatusId = null;
          // Émettre le mode de vue sélectionné
          this.$emit("calculation-complete", this.selectedViewMode);
        } catch (error) {
          // L'erreur est déjà gérée dans le store
        }
      }
    },
    goBack() {
      this.$emit("go-back");
    },
    clearError() {
      this.$store.commit("projections/setError", null);
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(value || 0);
    },
    formatNumber(value) {
      return new Intl.NumberFormat("fr-FR").format(Math.round(value || 0));
    },
    getStatusColor,
    getStatusLabel(status) {
      if (status === -1 || status === 0 || status === "-1" || status === "0") {
        return "Hors programme";
      }
      return status !== undefined && status !== null ? status.toString() : "0";
    },
  },
  watch: {
    form: {
      handler(newForm) {
        // Calcul automatique du total
        this.$store.commit("projections/updateProjectionForm", newForm);
      },
      deep: true,
    },
    selectedCustomer: {
      handler(newCustomer, oldCustomer) {
        // Recharger les stats quand le client change
        if (newCustomer && newCustomer.id !== oldCustomer?.id) {
          this.form.customerId = newCustomer.id;
          this.form.overrideStatusId = null;
          this.loadStats();
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.v-chip {
  font-weight: 500;
}
</style>
